import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { EventsTrendsDockTab } from "./EventsTrendsDockShell";

const OVERFLOW_CONTROL_WIDTH_PX = 30;
const TAB_GROUP_CHROME_PX = 28;

function estimateTabWidth(tab: EventsTrendsDockTab): number {
  const labelWidth = Math.max(24, tab.label.length * 6.2);
  return Math.ceil(labelWidth + TAB_GROUP_CHROME_PX);
}

export function useResponsiveDockTabs(options: {
  tabs: EventsTrendsDockTab[];
  enabled: boolean;
  tabStripRef: React.RefObject<HTMLElement | null>;
  utilitiesRef: React.RefObject<HTMLElement | null>;
  overflowToggleRef: React.RefObject<HTMLElement | null>;
  overflowMenuRef: React.RefObject<HTMLElement | null>;
}) {
  const {
    tabs,
    enabled,
    tabStripRef,
    utilitiesRef,
    overflowToggleRef,
    overflowMenuRef,
  } = options;
  const tabKeys = tabs.map((tab) => tab.panelKey).join("|");
  const [maxVisibleTabs, setMaxVisibleTabs] = useState(tabs.length);
  const [slotOrder, setSlotOrder] = useState<string[]>(() =>
    tabs.map((tab) => tab.panelKey),
  );
  const [overflowOpen, setOverflowOpen] = useState(false);

  useEffect(() => {
    setSlotOrder(tabs.map((tab) => tab.panelKey));
    setOverflowOpen(false);
  }, [tabKeys, tabs]);

  const recomputeVisibleCapacity = useCallback(() => {
    if (!enabled) {
      setMaxVisibleTabs(tabs.length);
      return;
    }

    const strip = tabStripRef.current;
    if (!strip) {
      setMaxVisibleTabs(tabs.length);
      return;
    }

    const utilitiesWidth = utilitiesRef.current?.offsetWidth ?? 0;
    const needsOverflowReserve = tabs.length > 1;
    let available =
      strip.clientWidth -
      utilitiesWidth -
      (needsOverflowReserve ? OVERFLOW_CONTROL_WIDTH_PX : 0);

    if (available <= 0) {
      setMaxVisibleTabs(1);
      return;
    }

    let used = 0;
    let visibleCount = 0;
    for (const tab of tabs) {
      const nextWidth = estimateTabWidth(tab);
      if (visibleCount > 0 && used + nextWidth > available) {
        break;
      }
      used += nextWidth;
      visibleCount += 1;
    }

    setMaxVisibleTabs(Math.max(1, Math.min(visibleCount, tabs.length)));
  }, [enabled, tabStripRef, tabs, utilitiesRef]);

  useLayoutEffect(() => {
    recomputeVisibleCapacity();
  }, [recomputeVisibleCapacity, tabKeys]);

  useEffect(() => {
    if (!enabled) return undefined;

    const strip = tabStripRef.current;
    if (!strip || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      recomputeVisibleCapacity();
    });
    observer.observe(strip);
    if (utilitiesRef.current) {
      observer.observe(utilitiesRef.current);
    }

    return () => observer.disconnect();
  }, [enabled, recomputeVisibleCapacity, tabStripRef, utilitiesRef]);

  const displaySlotKeys = useMemo(() => {
    if (!enabled || maxVisibleTabs >= tabs.length) {
      return tabs.map((tab) => tab.panelKey);
    }

    const activeKey = tabs.find((tab) => tab.isActive)?.panelKey;
    const normalizedOrder = [
      ...slotOrder.filter((key) => tabs.some((tab) => tab.panelKey === key)),
      ...tabs
        .map((tab) => tab.panelKey)
        .filter((key) => !slotOrder.includes(key)),
    ];

    let slots = normalizedOrder.slice(0, maxVisibleTabs);
    if (activeKey && !slots.includes(activeKey)) {
      slots = [...slots.slice(0, Math.max(0, maxVisibleTabs - 1)), activeKey];
    }

    return slots;
  }, [enabled, maxVisibleTabs, slotOrder, tabs]);

  const visibleTabs = useMemo(
    () =>
      displaySlotKeys
        .map((panelKey) => tabs.find((tab) => tab.panelKey === panelKey))
        .filter((tab): tab is EventsTrendsDockTab => Boolean(tab)),
    [displaySlotKeys, tabs],
  );

  const overflowTabs = useMemo(
    () => tabs.filter((tab) => !displaySlotKeys.includes(tab.panelKey)),
    [displaySlotKeys, tabs],
  );

  const replaceVisibleTab = useCallback(
    (panelKey: string, position: "first" | "last") => {
      setSlotOrder((previous) => {
        const normalized = [
          ...previous.filter((key) => tabs.some((tab) => tab.panelKey === key)),
          ...tabs
            .map((tab) => tab.panelKey)
            .filter((key) => !previous.includes(key)),
        ];
        const currentSlots = normalized.slice(0, maxVisibleTabs);
        const remainder = normalized.filter((key) => !currentSlots.includes(key));
        const slotIndex =
          position === "first"
            ? 0
            : Math.max(0, Math.min(maxVisibleTabs - 1, currentSlots.length - 1));
        const replacedKey = currentSlots[slotIndex];
        const nextSlots = [...currentSlots];
        nextSlots[slotIndex] = panelKey;

        const tail = remainder.filter(
          (key) => key !== panelKey && key !== replacedKey,
        );
        if (replacedKey && replacedKey !== panelKey) {
          tail.unshift(replacedKey);
        }

        return [...nextSlots, ...tail];
      });

      tabs.find((tab) => tab.panelKey === panelKey)?.onSelect();
      setOverflowOpen(false);
    },
    [maxVisibleTabs, tabs],
  );

  useEffect(() => {
    if (!overflowOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (overflowMenuRef.current?.contains(target)) return;
      if (overflowToggleRef.current?.contains(target)) return;
      setOverflowOpen(false);
    };

    const listenerId = window.setTimeout(() => {
      document.addEventListener("mousedown", handlePointerDown, true);
    }, 0);

    return () => {
      window.clearTimeout(listenerId);
      document.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, [overflowOpen, overflowMenuRef, overflowToggleRef]);

  return {
    visibleTabs,
    overflowTabs,
    showOverflow: enabled && overflowTabs.length > 0,
    overflowOpen,
    setOverflowOpen,
    replaceVisibleTab,
  };
}
